import { usePage } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FormError } from "@/components/FormError"
import { FlashMessages } from "@/components/FlashMessages"
import { AuthLayout } from "@/components/layouts/AuthLayout"

interface PageProps {
  fieldErrors?: Record<string, string>
  flash?: {
    notice?: string | null
    alert?: string | null
  }
  [key: string]: unknown
}

export default function Register() {
  const { t } = useTranslation()
  const { fieldErrors, flash } = usePage<PageProps>().props
  const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [confirmError, setConfirmError] = useState<string | null>(null)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    if (password !== confirmPassword) {
      e.preventDefault()
      setConfirmError(t("register.passwordsMustMatch"))
      return
    }
    setConfirmError(null)
  }

  return (
    <AuthLayout>
      <FlashMessages notice={flash?.notice} alert={flash?.alert} />
      <div className="eyebrow mb-2">{t("register.eyebrow", { defaultValue: "Comece agora" })}</div>
      <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tight text-foreground m-0">
        {t("register.title")}
      </h1>
      <p className="text-muted-foreground text-sm mt-2 mb-7">
        {t("register.subtitle")}
      </p>

      <form method="post" action="/create-account" onSubmit={handleSubmit} className="space-y-4">
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <div className="space-y-1.5">
          <Label htmlFor="name" className="mono-col">{t("register.nameLabel")}</Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder={t("register.namePlaceholder")}
          />
          <FormError error={fieldErrors?.name} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="mono-col">{t("register.emailLabel")}</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={t("register.emailPlaceholder")}
            required
          />
          <FormError error={fieldErrors?.email} />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="mono-col">{t("register.passwordLabel")}</Label>
          <Input
            id="password"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
          <FormError error={fieldErrors?.password} />
          <p className="text-xs text-muted-foreground">{t("register.passwordHint")}</p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password-confirm" className="mono-col">{t("register.confirmPasswordLabel")}</Label>
          <Input
            id="password-confirm"
            name="password-confirm"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value)
              setConfirmError(null)
            }}
            required
            minLength={8}
          />
          <FormError error={confirmError || fieldErrors?.["password-confirm"]} />
        </div>

        <Button type="submit" className="w-full" size="lg">
          {t("register.submit")}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-border" />
          <span className="font-mono text-[10px] uppercase text-muted-foreground tracking-[0.18em]">
            {t("login.or", { defaultValue: "ou" })}
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <p className="text-center text-sm text-muted-foreground">
          {t("register.hasAccount")}{" "}
          <a
            href="/login"
            className="font-semibold hover:underline"
            style={{ color: "var(--accent)" }}
          >
            {t("register.signIn")}
          </a>
        </p>
      </form>
    </AuthLayout>
  )
}
